import React, { useState, useEffect } from 'react'
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid'
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Checkbox,
  Tooltip,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { withFirebase } from '../../components/Fb' // Adjust path as needed
import { paymentText } from '../../components/utils/purchases'
import { endsBeforeStart } from '../../components/utils/benefitcards'

function CardListExportToolbar() {
  return (
    <GridToolbarContainer style={{ justifyContent: 'flex-start' }}>
      <GridToolbarFilterButton />
      <GridToolbarExport
        csvOptions={{
          fileName: 'korttilistaus',
          delimiter: ';',
          utf8WithBom: true,
        }}
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  )
}

const getBenefitTypeLabel = (type) => {
  switch (type) {
    case 'time':
      return 'Aika'
    case 'count':
      return 'Kerta'
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}

const getBenefitCardCollectionLabel = (collection) => {
  switch (collection) {
    case 'activities':
      return 'Aktiviteetit'
    case 'reservations':
      return 'Varaukset'
    default:
      return collection ? collection.charAt(0).toUpperCase() + collection.slice(1) : 'Varaukset'
  }
}

const columns = [
  { field: 'name', headerName: 'Kortti', flex: 1, filterable: false },
  {
    field: 'type',
    headerName: 'Tyyppi',
    flex: 1,
    valueFormatter: (value) => getBenefitTypeLabel(value),
    filterable: false,
  },
  {
    field: 'cardcollection',
    headerName: 'Kortin käyttökohde',
    flex: 1,
    filterable: false,
    valueFormatter: (value) => getBenefitCardCollectionLabel(value),
  },
  {
    field: 'user',
    headerName: 'Henkilö',
    flex: 1,
    // Parenthesised: `a + ' ' + b || 'N/A'` binds the || to b alone, so a row with no user
    // renders 'undefined undefined' instead of the fallback.
    valueGetter: (value, row) =>
      row.user ? row.user.firstname + ' ' + row.user.lastname : 'N/A',
    filterable: true,
    sortable: true,
  },
  {
    field: 'email',
    headerName: 'Sähköposti',
    flex: 1,
    minWidth: 200,
    // Already embedded on the purchase alongside the name - no join, no extra fetch.
    valueGetter: (value, row) => (row.user && row.user.email) || '',
  },
  {
    field: 'price',
    headerName: 'Hinta',
    flex: 1,
    // Prefer the actual paid amount from the payment data (cents). Supports both
    // a single `payment` object and a `payments` array; falls back to `price`.
    valueGetter: (value, row) => {
      let cents = null
      if (Array.isArray(row.payments) && row.payments.length > 0) {
        cents = row.payments.reduce((a, p) => a + (p.amount || 0), 0)
      } else if (row.payment && row.payment.amount != null) {
        cents = row.payment.amount
      } else if (row.price != null) {
        cents = row.price
      }
      return cents == null ? null : cents / 100
    },
    valueFormatter: (value) => (value == null ? '-' : `${value} €`),
  },
  {
    field: 'payment',
    headerName: 'Maksu',
    flex: 1.4,
    // 310, not the 220 the memberships list uses: 'Online (Paytrail/osuuspankki)' is 29
    // characters before the amount and date are prepended, and a cell that truncates
    // mid-method loses the one part of the three that no other column carries. Wide enough
    // for the longest label the payment providers produce; the grid scrolls horizontally on
    // a narrow window, which it already does with nine columns.
    minWidth: 310,
    // Hinta above answers "what is this worth", preferring the paid amount but falling back
    // to the list price - so it cannot distinguish a paid card from a staff-added one. This
    // column answers "was it actually paid, how, and when", and says so in words when it
    // was not. In the valueGetter rather than a renderCell so the CSV carries the text too:
    // MUI does not export rendered cells.
    //
    // Date without the clock time: this grid has nine columns, and the full string truncated
    // mid-method - losing the part that cannot be recovered from any other column, while
    // Ostettu / Luotu already carries the purchase moment for anyone who needs it.
    valueGetter: (value, row) => paymentText(row, { withTime: false }),
  },
  {
    field: 'timestamp',
    headerName: 'Ostettu / Luotu',
    flex: 1,
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'start',
    headerName: 'Aloitus',
    flex: 1,
    valueFormatter: (value) => new Date(value).toLocaleDateString(),
  },
  {
    field: 'end',
    headerName: 'Loppuu',
    flex: 1,
    valueGetter: (value, row) => {
      if (row.validuntil) {
        // Backend sentinel for a card with no end date, same as PersonBenefitCards shows.
        if (row.validuntil === '2300-01-01') return 'Ei päättymisaikaa'
        return new Date(row.validuntil).toLocaleDateString()
      } else if (row.end) {
        return new Date(row.end).toLocaleDateString()
      } else {
        return ''
      }
    },
    // The valueGetter above is left alone so the exported cell stays a clean date; the
    // warning is display only. Ranges that end before they start do occur in live data.
    renderCell: (params) => {
      if (!endsBeforeStart(params.row)) return params.value
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
          <Tooltip title='Päättymispäivä on ennen alkamispäivää'>
            <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }} />
          </Tooltip>
        </Box>
      )
    },
  },
  {
    field: 'remaining',
    headerName: 'Jäljellä',
    flex: 1,
    valueGetter: (value, row) => {
      if (row.type === 'time') {
        return row.remaining ? `${row.remaining} min` : '-'
      } else if (row.type === 'count') {
        return row.remaining ? `${row.remaining} krt` : '-'
      }
    },
  },
  // {
  //   field: 'edit',
  //   headerName: 'Edit',
  //   flex: 1,
  //   disableClickEventBubbling: true,
  //   renderCell: (params) => (
  //     <EditButton
  //       loading={params.api.getRow(params.row.id).loading}
  //       onClick={() => params.api.getRow(params.row.id).onEdit(params.row.id)}
  //     />
  //   ),
  // },
]

const BenefitCardListsContainer = ({ firebase }) => {
  const [benefitCardsData, setBenefitCardsData] = useState([])
  const [filterCardIds, setFilterCardIds] = useState([])
  const [filteredCards, setFilteredCards] = useState([])
  const [cBenefitCards, setCBenefitCards] = useState([])

  const handleFilterChange = (event) => {
    const {
      target: { value },
    } = event
    const selectedIds = value.map((v) =>
      cBenefitCards.find((mem) => mem.value === v)
    )
    setFilterCardIds(selectedIds)
  }

  useEffect(() => {
    Promise.all([
      firebase.getPurchasedBenefitCards(), // Adjust method as needed
      firebase.getBenefitCards(),
    ])
      .then(([benefitCards, customerBenefitCards]) => {
        setBenefitCardsData(benefitCards)
        if (!customerBenefitCards.empty) {
          customerBenefitCards = customerBenefitCards.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        }
        setFilteredCards(benefitCards)
        setCBenefitCards([
          { label: 'Kaikki', value: 'Kaikki' },
          ...Array.from(
            new Set(
              customerBenefitCards.map((card) => {
                return { label: card.name, value: card.id }
              })
            )
          ),
        ])
      })
      .catch((error) => {
        console.error('Error fetching benefit cards:', error)
      })
  }, [firebase])

  useEffect(() => {
    if (filterCardIds.length === 0 || filterCardIds[0].value === 'Kaikki') {
      setFilteredCards(benefitCardsData)
    } else {
      setFilteredCards(
        benefitCardsData.filter((card) =>
          filterCardIds.some((fMem) => fMem.value === card.benefitcardid)
        )
      )
    }
  }, [filterCardIds, benefitCardsData])

  return (
     <div className='content' style={{ padding: 20, paddingTop: 10 }}>
      <Paper sx={{ p: 3, m: 2, mt: 0 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id='benefit-type-filter-label'>Näytä kortit</InputLabel>
            <Select
              labelId='benefit-type-filter-label'
              multiple
              value={filterCardIds.map((mem) => mem.value)}
              label='Näytä kortit'
              onChange={handleFilterChange}
              renderValue={(selected) =>
                selected
                  .map((value) => {
                    const card = cBenefitCards.find((c) => c.value === value)
                    return card ? card.label : ''
                  })
                  .join(', ')
              }
            >
              {cBenefitCards.map((card) => {
                const isChecked = filterCardIds.some(
                  (fCard) => fCard.value === card.value
                )
                return (
                  <MenuItem key={card.value} value={card.value}>
                    <Checkbox checked={isChecked} />
                    {card.label}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ height: '100%', width: '100%' }}>
          <div style={{ display: 'flex', height: '100%' }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              {/* DataGrid is v8: pageSize/onPageSizeChange/rowsPerPageOptions are the v5
                  names, removed in v7, so the page size selector did nothing. Same family
                  of rename as the components -> slots one that had hidden this toolbar. */}
              <DataGrid
                autoHeight
                density='compact'
                pagination
                rows={filteredCards}
                columns={columns}
                showToolbar
                slots={{ toolbar: CardListExportToolbar }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 50 } },
                }}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
              />
            </div>
          </div>
        </Box>
      </Paper>
    </div>
  )
}

export default withFirebase(BenefitCardListsContainer)
